import React from 'react';
import type { CrudEditModalProps } from '../crud/CrudTable';
import useLoadTracker from '@dr-pam/common-components/Hooks/useLoadTracker';
import { Button, Container, Group, Loader, Stack, Switch, TextInput, Divider } from '@mantine/core';
import { useForm, schemaResolver } from '@mantine/form';
import { modals } from '@mantine/modals';
import NotificationUtils from '@dr-pam/common-components/Utils/NotificationUtils';
import type { UserForUpdate } from '../../services/UserService';
import { useUserService } from '../../services/UserService';
import type { User } from '@dr-pam/common-types/database/client';
import z from 'zod';

const validationSchema = z.object({
	email: z.string().nonempty('Required').email().max(512),
	displayName: z.string().max(512),
	fullName: z.string().nonempty().max(512),
	stripeCustomerId: z.string().max(512).optional().or(z.literal('')),
	stripePaymentMethodId: z.string().max(512).optional().or(z.literal('')),
	isListedOnNdcRegister: z.boolean(),
	isNdcLactationFellow: z.boolean(),
	isMedicalPractitioner: z.boolean(),
});

export type UpdateUserModalProps = CrudEditModalProps<User, User>;

// Only show Stripe fields in beta/local environments
const isBetaOrLocalEnvironment =
	typeof window !== 'undefined' &&
	(window.location.hostname.includes('beta') || window.location.hostname === 'localhost');

export default function UpdateUserModal(props: UpdateUserModalProps) {
	const { modalId, onCancel, onEdited, current } = props;

	const { addLoader, removeLoader, isLoading } = useLoadTracker();

	const userService = useUserService();

	const form = useForm<UpdateUserForm>({
		initialValues: {
			email: current.email,
			fullName: current.fullName,
			displayName: current.displayName ?? '',
			stripeCustomerId: current.stripeCustomerId ?? '',
			stripePaymentMethodId: current.stripePaymentMethodId ?? '',
			isListedOnNdcRegister: current.isListedOnNdcRegister ?? false,
			isNdcLactationFellow: current.isNdcLactationFellow,
			isMedicalPractitioner: current.isMedicalPractitioner,
		},
		validate: schemaResolver(validationSchema),
	});

	const handleSubmit = async (values: UpdateUserForm) => {
		const loader = addLoader();

		try {
			const request = userService.update(current.id, {
				id: current.id,
				email: values.email || undefined,
				fullName: values.fullName,
				displayName: values.displayName || undefined,
				// Sent only when the admin changed the switch, so a member who has not chosen yet stays unset
				isListedOnNdcRegister: form.isTouched('isListedOnNdcRegister')
					? values.isListedOnNdcRegister
					: undefined,
				isNdcLactationFellow: values.isNdcLactationFellow,
				isMedicalPractitioner: values.isMedicalPractitioner,
				// Only include Stripe fields in beta/local environments
				...(isBetaOrLocalEnvironment && {
					stripeCustomerId: values.stripeCustomerId === '' ? null : values.stripeCustomerId,
					stripePaymentMethodId: values.stripePaymentMethodId === '' ? null : values.stripePaymentMethodId,
				}),
			});
			const updatedUser = await request.response;
			onEdited(updatedUser);
			NotificationUtils.showSuccess('Successfully updated user', updatedUser.displayName ?? updatedUser.email);
			modals.close(modalId);
		} catch (err) {
			NotificationUtils.showError(err as Error, 'Failed to update user');
			removeLoader(loader);
		}
	};

	const handleCancel = () => {
		onCancel?.();
		modals.close(modalId);
	};

	return (
		<Container>
			<form onSubmit={form.onSubmit(handleSubmit)}>
				<Stack>
					<TextInput
						{...form.getInputProps('email')}
						type="email"
						label="Email"
						description="The email of the user"
						disabled={isLoading}
					/>
					<TextInput
						{...form.getInputProps('fullName')}
						label="Full name"
						description="The full name of the user"
						disabled={isLoading}
					/>
					<TextInput
						{...form.getInputProps('displayName')}
						label="Display name"
						description="First name or nickname"
						disabled={isLoading}
					/>

					<Divider my="sm" label="NDC Register" labelPosition="center" />

					{/* withFocus: false so tabbing onto the switch does not count as a choice */}
					<Switch
						{...form.getInputProps('isListedOnNdcRegister', { type: 'checkbox', withFocus: false })}
						label="List on The NDC Register"
						description="Shown on possums.org/ndc-status while their NDC qualification is current"
						disabled={isLoading}
					/>
					<Switch
						{...form.getInputProps('isNdcLactationFellow', { type: 'checkbox' })}
						label="NDC Lactation Fellow"
						description="Gives the NDC Lactation Fellow title and mark on The NDC Register"
						disabled={isLoading}
					/>
					<Switch
						{...form.getInputProps('isMedicalPractitioner', { type: 'checkbox' })}
						label="Medical practitioner"
						description="Their Lactation Fellowship certificate reads 'for Medical Practitioners'; otherwise 'for Registered Health Professionals'"
						disabled={isLoading}
					/>

					{isBetaOrLocalEnvironment && (
						<>
							<Divider my="sm" label="Stripe Integration" labelPosition="center" />

							<TextInput
								{...form.getInputProps('stripeCustomerId')}
								label="Stripe Customer ID"
								description="Leave empty to clear. New customer will be created on next checkout."
								placeholder="cus_..."
								disabled={isLoading}
							/>
							<TextInput
								{...form.getInputProps('stripePaymentMethodId')}
								label="Stripe Payment Method ID"
								description="Leave empty to clear. User can add new payment methods from their account."
								placeholder="pm_..."
								disabled={isLoading}
							/>
						</>
					)}

					<Group justify="flex-end" mt="md">
						<Button type="button" variant="subtle" onClick={handleCancel} disabled={isLoading}>
							Cancel
						</Button>
						<Button type="submit" disabled={isLoading}>
							Save user {isLoading && <Loader size="xs" ml="xs" />}
						</Button>
					</Group>
				</Stack>
			</form>
		</Container>
	);
}

export type UpdateUserForm = Pick<
	UserForUpdate,
	'email' | 'displayName' | 'fullName' | 'stripeCustomerId' | 'stripePaymentMethodId'
> & {
	isListedOnNdcRegister: boolean;
	isNdcLactationFellow: boolean;
	isMedicalPractitioner: boolean;
};
